import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { CalendarClock, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#B27516', red: '#E24B4A' };
const TONE = { ok: C.green, accepted: C.blue, running: C.blue, refused: C.slate, skipped: C.slate, duplicate: C.slate, blocked: C.amber, timeout: C.amber, error: C.red };
const when = iso => (iso ? new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

// The five model-calling workers as bounded scheduled jobs: one sweep per tick under a run-parameter sheet.
export function BoundedJobsCard() {
  const api = useApi();
  const { user } = useAuth();
  const canRun = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api.get('/jobs').then(r => setData(r.data)).catch(() => setData(null)), [api]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const run = async job => {
    setBusy(job);
    try {
      const { data: r } = await api.post(`/jobs/${job}/run`, {});
      toast[r.status === 'accepted' ? 'success' : 'message'](`${job}: ${r.status}${r.stop_reason ? ` — ${r.stop_reason}` : ''}`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not run'); } finally { setBusy(''); }
  };
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="bounded-jobs-card">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarClock className="w-4 h-4" style={{ color: C.blue }} />
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Bounded scheduled jobs</p>
        <span className="text-[10px] font-mono" style={{ color: C.slate }}>{data.env} · {data.mode} mode · platform scheduler ticks, nothing loops</span>
      </div>
      <table className="w-full text-[11px] mt-2">
        <thead><tr style={{ color: C.slate }}><th className="text-left font-semibold py-1">Job</th><th className="text-left font-semibold py-1">Schedule</th><th className="text-left font-semibold py-1">Switch</th><th className="text-left font-semibold py-1">Last tick</th><th className="text-right font-semibold py-1">Caps</th><th className="w-16" /></tr></thead>
        <tbody>
          {data.jobs.map(j => {
            const last = j.last_run;
            const tone = TONE[last?.status] || C.slate;
            return (
              <tr key={j.job} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`bounded-job-${j.job}`}>
                <td className="py-1.5 pr-2"><p className="font-semibold" style={{ color: C.navy }}>{j.job}</p><p className="text-[10px] font-mono" style={{ color: C.slate }}>sheet v{j.sheet_version} · {j.worker}</p></td>
                <td className="py-1.5 pr-2" style={{ color: C.slate }}>{j.schedule}</td>
                <td className="py-1.5 pr-2">
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase" style={{ background: j.switch === 'on' ? 'rgba(29,158,117,0.12)' : 'rgba(92,107,122,0.12)', color: j.switch === 'on' ? C.green : C.slate }} data-testid={`bounded-job-switch-${j.job}`}>
                    {j.switch}{j.quieted_by_build_mode ? ' · build mode' : j.ruling === 'off' ? ' · ruling' : ''}
                  </span>
                </td>
                <td className="py-1.5 pr-2" data-testid={`bounded-job-last-${j.job}`}>
                  {last ? <><span className="font-semibold" style={{ color: tone }}>{last.status}</span><span style={{ color: C.slate }}> · {when(last.accepted_at)} · {last.dispatch}{last.items != null ? ` · ${last.items} items` : ''}{last.stop_reason && last.status !== 'ok' ? ` · ${last.stop_reason}` : ''}</span></> : <span style={{ color: C.slate }}>never</span>}
                </td>
                <td className="py-1.5 text-right font-mono text-[10px]" style={{ color: C.slate }}>{typeof j.max_items === 'number' ? `${j.max_items} items` : j.max_items} · {j.max_runtime_s}s</td>
                <td className="py-1.5 text-right">{canRun && <button onClick={() => run(j.job)} disabled={busy === j.job || j.running} className="px-2 py-1 rounded-sm text-[11px] font-semibold flex items-center gap-1 ml-auto disabled:opacity-50" style={{ background: 'rgba(26,76,124,0.1)', color: C.blue }} data-testid={`bounded-job-run-${j.job}`}><Play className="w-3 h-3" />tick</button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[10px] mt-2" style={{ color: C.slate }}>{data.note}</p>
    </Card>
  );
}
